/**
 * Rejection codes: the closed set from doc 02 §2.9 (18 ratified) plus the
 * additions in doc 02 §2.10, ratified by decisions B1 and B9 (doc 06 §7).
 *
 * Closed set. Every rejection returns exactly one of these; adding one is a
 * spec change (doc 02 §2.9 header). `describeRejection` is an exhaustive
 * `switch`, so adding a member to `REJECTION_CODES` without categorising it
 * is a compile error, not a runtime surprise.
 */

/** The 18 codes ratified in doc 02 §2.9. */
export const RATIFIED_REJECTION_CODES = [
  'E_MARKET_CLOSED',
  'E_SYMBOL_DISABLED',
  'E_NO_QUOTE',
  'E_INVALID_VOLUME',
  'E_INVALID_PRICE',
  'E_PRICE_FAR',
  'E_NO_MONEY',
  'E_MAX_POSITIONS',
  'E_VOLUME_LIMIT',
  'E_TIF_NOT_ALLOWED',
  'E_ORDER_TYPE_NOT_ALLOWED',
  'E_DUPLICATE_CL_ORD_ID',
  'E_ACCOUNT_DISABLED',
  'E_TRADE_DISABLED',
  'E_NOT_ENOUGH_MARGIN_FOR_PENDING',
  'E_PRICE_SLIPPAGE',
  'E_DEALER_REJECTED',
  'E_INTERNAL',
] as const;

/** The five codes added by doc 02 §2.10 / decisions B1 and B9. Grows 18 -> 23. */
export const ADDED_REJECTION_CODES = [
  /** doc 02 §3.8 — `FOK` could not be filled in full. */
  'E_FILL_OR_KILL',
  /** doc 02 §3.7 — `EXCHANGE` mode venue did not confirm in time. */
  'E_EXTERNAL_TIMEOUT',
  /** doc 03 §2.1 rule 4 — no conversion path to the account currency. */
  'E_NO_CONVERSION',
  /** doc 04 §7 — capacity limit; a platform condition, never a trading rule. */
  'E_TENANT_QUOTA',
  /** doc 02 §2.10 / decision B9 — at the margin-call level; new exposure refused. */
  'E_MARGIN_CALL',
  /**
   * docs/10 §9.2 item 1 — a command the order's **state** cannot accept, e.g.
   * cancelling an order that is already `FILLED`. It is a client mistake, not a
   * protocol violation and not a defect: doc 02 §3.3 calls the transition
   * forbidden, and docs/10 requires it to surface as `RESPONSE{REJECTED}` with
   * an order-state code rather than as a protocol error. Adding it grows the
   * closed set — a spec change recorded in docs/10 §9.2, not a silent extension.
   */
  'E_ORDER_STATE',
  /**
   * docs/19 §3 P12.1 — the market moved further than the group's `deviation_e8`
   * between the price the client named and the price available now, so the
   * order was refused **before** anything was written and may be retried with
   * the same `cl_ord_id` (at the new price, or with a wider tolerance).
   *
   * Deliberately distinct from its neighbours, because the operator action
   * differs for each: `E_PRICE_SLIPPAGE` is a *requote* the client may accept,
   * `E_NO_QUOTE` is "prices are unavailable", `E_PRICE_FAR` is "your pending
   * price is too close to the market". Collapsing them would make the desk read
   * "price moved" as a feed fault.
   */
  'E_PRICE_CHANGED',
  /**
   * docs/19 §3 P12.5 — the serving source changed and the two feeds' prices differ
   * by more than the symbol's cross-source tolerance, so **entries** are refused
   * until the cooldown expires. Closes are always allowed.
   *
   * Distinct from every neighbour: not `E_NO_QUOTE` (there is a price, and the
   * client can see it), not `E_PRICE_CHANGED` (nothing moved against the price the
   * client named), not `E_MARKET_CLOSED` (the venue is open; the *picture* is
   * inconsistent). A desk reading "price changed" for a feed disagreement would
   * look in the wrong place.
   */
  'E_CROSS_SOURCE_GUARD',
] as const;

/** The complete ratified set: 26 codes, no duplicates. */
export const REJECTION_CODES = [...RATIFIED_REJECTION_CODES, ...ADDED_REJECTION_CODES] as const;

export type RejectionCode = (typeof REJECTION_CODES)[number];

export type RatifiedRejectionCode = (typeof RATIFIED_REJECTION_CODES)[number];
export type AddedRejectionCode = (typeof ADDED_REJECTION_CODES)[number];

/** 18 + 5 + the order-state code + P12.1's price change + P12.5's guard. */
export const REJECTION_CODE_COUNT = 26;

/** How a rejection is presented and routed. */
export type RejectionCategory =
  | 'MARKET'
  | 'PRICE'
  | 'ORDER'
  | 'MARGIN'
  | 'ACCOUNT'
  | 'DEALER'
  | 'PLATFORM'
  | 'INTERNAL';

export interface RejectionMeta {
  readonly code: RejectionCode;
  readonly category: RejectionCategory;
  /** Where the code is specified, for the "why was I rejected" audit trail. */
  readonly doc: string;
}

/** Compile-time exhaustiveness helper for `switch` statements over codes. */
export function assertNever(value: never): never {
  throw new Error(`Unhandled variant: ${JSON.stringify(value)}`);
}

/** Exhaustive categorisation. Every `RejectionCode` has exactly one arm. */
export function describeRejection(code: RejectionCode): RejectionMeta {
  switch (code) {
    case 'E_MARKET_CLOSED':
      return { code, category: 'MARKET', doc: 'doc 02 §2.9' };
    case 'E_SYMBOL_DISABLED':
      return { code, category: 'MARKET', doc: 'doc 02 §2.9' };
    case 'E_NO_QUOTE':
      return { code, category: 'MARKET', doc: 'doc 02 §2.9' };
    case 'E_INVALID_PRICE':
      return { code, category: 'PRICE', doc: 'doc 02 §2.9' };
    case 'E_PRICE_FAR':
      return { code, category: 'PRICE', doc: 'doc 02 §2.9' };
    case 'E_PRICE_SLIPPAGE':
      return { code, category: 'PRICE', doc: 'doc 02 §2.9' };
    case 'E_PRICE_CHANGED':
      return { code, category: 'PRICE', doc: 'doc 02 §2.10' };
    case 'E_CROSS_SOURCE_GUARD':
      return { code, category: 'MARKET', doc: 'doc 19 §3' };
    case 'E_INVALID_VOLUME':
      return { code, category: 'ORDER', doc: 'doc 02 §2.9' };
    case 'E_VOLUME_LIMIT':
      return { code, category: 'ORDER', doc: 'doc 02 §2.9' };
    case 'E_MAX_POSITIONS':
      return { code, category: 'ORDER', doc: 'doc 02 §2.9' };
    case 'E_TIF_NOT_ALLOWED':
      return { code, category: 'ORDER', doc: 'doc 02 §2.9' };
    case 'E_ORDER_TYPE_NOT_ALLOWED':
      return { code, category: 'ORDER', doc: 'doc 02 §2.9' };
    case 'E_DUPLICATE_CL_ORD_ID':
      return { code, category: 'ORDER', doc: 'doc 02 §2.9' };
    case 'E_FILL_OR_KILL':
      return { code, category: 'ORDER', doc: 'doc 02 §2.10' };
    case 'E_NO_MONEY':
      return { code, category: 'MARGIN', doc: 'doc 02 §2.9' };
    case 'E_NOT_ENOUGH_MARGIN_FOR_PENDING':
      return { code, category: 'MARGIN', doc: 'doc 02 §2.9' };
    case 'E_MARGIN_CALL':
      return { code, category: 'MARGIN', doc: 'doc 02 §2.10' };
    case 'E_NO_CONVERSION':
      return { code, category: 'MARGIN', doc: 'doc 03 §2.1 rule 4' };
    case 'E_ACCOUNT_DISABLED':
      return { code, category: 'ACCOUNT', doc: 'doc 02 §2.9' };
    case 'E_TRADE_DISABLED':
      return { code, category: 'ACCOUNT', doc: 'doc 02 §2.9' };
    case 'E_DEALER_REJECTED':
      return { code, category: 'DEALER', doc: 'doc 02 §2.9' };
    case 'E_EXTERNAL_TIMEOUT':
      return { code, category: 'PLATFORM', doc: 'doc 02 §2.10' };
    case 'E_TENANT_QUOTA':
      return { code, category: 'PLATFORM', doc: 'doc 04 §7' };
    case 'E_ORDER_STATE':
      return { code, category: 'ORDER', doc: 'doc 10 §9.2 item 1' };
    case 'E_INTERNAL':
      return { code, category: 'INTERNAL', doc: 'doc 02 §2.9' };
    default:
      return assertNever(code);
  }
}

export function isRejectionCode(value: unknown): value is RejectionCode {
  return typeof value === 'string' && (REJECTION_CODES as readonly string[]).includes(value);
}

export function isRatifiedRejectionCode(value: unknown): value is RatifiedRejectionCode {
  return typeof value === 'string' && (RATIFIED_REJECTION_CODES as readonly string[]).includes(value);
}
